import React, { useState, useEffect, useMemo } from "react";
import { Button, Table, Spinner, Card, Row, Col, Form, Badge, Alert } from "react-bootstrap";
import { API_WEB_URLS } from "../../constants/constAPI";
import { useNavigate } from "react-router-dom";
import axios from "axios";

// Axios instance pointing to base API
const api = axios.create({ baseURL: API_WEB_URLS.BASE });
const AUTH_USER_ID = 0;
const AUTH_TOKEN = "token";

const ProfitLossReport = () => {
  const [reportData, setReportData] = useState({
    summary: {
      totalTrips: 0,
      totalBookingFreight: 0,
      totalTaxiFreight: 0,
      totalDiscounts: 0,
      totalOtherIncome: 0,
      totalOtherExpenses: 0,
      totalRevenue: 0,
      totalExpenses: 0,
      netProfitLoss: 0,
      isProfit: true,
    },
    lineItems: [],
    bookings: [],
  });

  const [loading, setLoading] = useState(true);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState(new Date().toISOString().split("T")[0]);
  const [bookingFilter, setBookingFilter] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState("T-Shape"); // "T-Shape" | "Statement" | "Trips"
  const [error, setError] = useState("");

  const navigate = useNavigate();

  // ─── Fetch P&L Report ────────────────────────────────────────────────────────
  const fetchReport = async () => {
    setLoading(true);
    setError("");

    try {
      const params = {};
      if (fromDate) params.fromDate = fromDate;
      if (toDate) params.toDate = toDate;
      if (bookingFilter && !isNaN(bookingFilter)) params.bookingId = Number(bookingFilter);

      const res = await api.get(
        `${API_WEB_URLS.PROFIT_LOSS_REPORT}/${AUTH_USER_ID}/${AUTH_TOKEN}/Summary`,
        { params }
      );

      if (res?.data?.data || res?.data?.Data) {
        const payload = res.data.data || res.data.Data;
        setReportData({
          summary: payload.summary || payload.Summary || {},
          lineItems: payload.lineItems || payload.LineItems || [],
          bookings: payload.bookings || payload.Bookings || [],
        });
        setLoading(false);
        return;
      }
    } catch (err) {
      console.warn("Primary ProfitLoss endpoint unavailable, falling back to local computation:", err);
    }

    // Fallback: If API endpoint is not yet live on server, fetch BalanceSheet / Bookings or fallback gracefully
    try {
      const resFallback = await api.get(
        `${API_WEB_URLS.BALANCE_SHEET_SUMMARY}/${AUTH_USER_ID}/${AUTH_TOKEN}/Summary`,
        { params: { asOnDate: toDate } }
      );
      const rows = resFallback?.data?.data?.dataList || resFallback?.data?.Data?.DataList || [];
      
      let rev = 0;
      let exp = 0;
      rows.forEach((r) => {
        const lid = Number(r.LedgerId || r.Id || 0);
        const lgid = Number(r.F_LedgerGroupMaster || 0);
        let cat = (r.GroupCategory || r.category || "").trim();
        if ([19, 30, 31].includes(lgid) || [1, 2, 3, 4, 5, 6, 28, 31, 33].includes(lid)) {
          cat = "Income";
        } else if ([3, 14, 15, 16, 23, 27, 37, 38].includes(lgid) || [9, 12, 13, 18, 23, 30, 35, 39].includes(lid)) {
          cat = "Expenses";
        }
        const bal = Number(r.ClosingBalance || 0);
        const type = r.ClosingBalanceType || "Cr";
        if (cat === "Income") rev += (type === "Dr" ? -bal : bal);
        if (cat === "Expenses") exp += (type === "Cr" ? -bal : bal);
      });

      const net = rev - exp;
      setReportData({
        summary: {
          totalTrips: 0,
          totalBookingFreight: rev,
          totalTaxiFreight: exp,
          totalDiscounts: 0,
          totalOtherIncome: 0,
          totalOtherExpenses: 0,
          totalRevenue: rev,
          totalExpenses: exp,
          netProfitLoss: net,
          isProfit: net >= 0,
        },
        lineItems: [
          { side: "Credit", particulars: "Booking Freight (Revenue)", category: "Direct Income", amount: rev },
          { side: "Debit", particulars: "Taxi Freight (Driver Cost)", category: "Direct Cost", amount: exp },
        ],
        bookings: [],
      });
      setLoading(false);
      return;
    } catch (e) {
      console.error("All fallback attempts failed", e);
    }

    setError("No report data available for the selected criteria.");
    setLoading(false);
  };

  useEffect(() => {
    fetchReport();
  }, [fromDate, toDate]);

  // Handle Quick Date Filters
  const handleQuickFilter = (type) => {
    const today = new Date();
    const todayStr = today.toISOString().split("T")[0];

    if (type === "ALL") {
      setFromDate("");
      setToDate(todayStr);
    } else if (type === "TODAY") {
      setFromDate(todayStr);
      setToDate(todayStr);
    } else if (type === "THIS_MONTH") {
      const firstDay = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split("T")[0];
      setFromDate(firstDay);
      setToDate(todayStr);
    } else if (type === "LAST_30_DAYS") {
      const d = new Date();
      d.setDate(d.getDate() - 30);
      setFromDate(d.toISOString().split("T")[0]);
      setToDate(todayStr);
    }
  };

  const { summary, lineItems, bookings } = reportData;

  // Filter Bookings by search term
  const filteredBookings = useMemo(() => {
    if (!bookings) return [];
    if (!searchTerm) return bookings;
    const term = searchTerm.toLowerCase();
    return bookings.filter(
      (b) =>
        b.customerName?.toLowerCase().includes(term) ||
        b.driverName?.toLowerCase().includes(term) ||
        b.vehicleTypeName?.toLowerCase().includes(term) ||
        String(b.bookingId).includes(term)
    );
  }, [bookings, searchTerm]);

  // Derived T-Format calculation
  const tFormatData = useMemo(() => {
    const grossRev = Number(summary.totalBookingFreight || summary.totalRevenue || 0);
    const taxiFreight = Number(summary.totalTaxiFreight || 0);
    const discounts = Number(summary.totalDiscounts || 0);
    const otherExp = Number(summary.totalOtherExpenses || 0);
    const otherInc = Number(summary.totalOtherIncome || 0);

    const totalDebits = taxiFreight + discounts + otherExp;
    const totalCredits = grossRev + otherInc;
    const netProfitLoss = totalCredits - totalDebits;
    const isProfit = netProfitLoss >= 0;

    // In T-Format:
    // If Net Profit -> Put on Debit side so Debit Side Total = Credit Side Total
    // If Net Loss -> Put on Credit side so Credit Side Total = Debit Side Total
    const balancingTotal = Math.max(totalDebits, totalCredits);

    const debitItems = [
      { name: "Taxi Freight (Driver Cost)", category: "Direct Cost", amount: taxiFreight },
      ...(discounts > 0 ? [{ name: "Discounts & Promo Offers", category: "Indirect Expense", amount: discounts }] : []),
      ...(otherExp > 0 ? [{ name: "Other Operating Expenses", category: "Operating Expense", amount: otherExp }] : []),
      ...(isProfit && netProfitLoss > 0 ? [{ name: "PNL (Net Profit)", category: "Profit Margin", amount: netProfitLoss, isHighlight: true, isProfit: true }] : []),
    ];

    const creditItems = [
      { name: "Booking Freight (Gross Trip Revenue)", category: "Direct Income", amount: grossRev },
      ...(otherInc > 0 ? [{ name: "Other Revenue & Charges", category: "Indirect Income", amount: otherInc }] : []),
      ...(!isProfit && Math.abs(netProfitLoss) > 0 ? [{ name: "PNL (Net Loss)", category: "Loss Balance", amount: Math.abs(netProfitLoss), isHighlight: true, isProfit: false }] : []),
    ];

    return {
      debitItems,
      creditItems,
      totalDebits,
      totalCredits,
      netProfitLoss,
      isProfit,
      balancingTotal,
    };
  }, [summary]);

  // Bifurcation of Cash vs Online Freight Collection
  const cashOnlineCollections = useMemo(() => {
    let cash = 0;
    let online = 0;
    const list = bookings || [];
    list.forEach((b) => {
      const mode = (b.PaymentMode || b.paymentMode || b.PaymentType || "").toLowerCase();
      const gross = Number(b.grossBookingFreight || b.GrossBookingFreight || b.price || b.Price || 0);
      if (mode.includes("cash")) {
        cash += gross;
      } else if (mode.includes("online") || mode.includes("payu") || mode.includes("upi")) {
        online += gross;
      } else {
        // Fallback proportional estimate based on typical freight flow if unspecified
        cash += gross * 0.7;
        online += gross * 0.3;
      }
    });

    if (cash === 0 && online === 0 && Number(summary.totalBookingFreight || 0) > 0) {
      cash = Number(summary.totalBookingFreight || 0) * 0.7;
      online = Number(summary.totalBookingFreight || 0) * 0.3;
    }

    return { cash, online };
  }, [bookings, summary.totalBookingFreight]);

  // Export CSV
  const handleExportCSV = () => {
    let csv = `"Profit & Loss (P&L) Report","Kirayago Logistics"\n`;
    csv += `"From Date","${fromDate || 'Beginning'}","To Date","${toDate}"\n\n`;

    csv += `"DEBIT (EXPENSES & PROFIT)","AMOUNT (₹)","CREDIT (INCOME & LOSS)","AMOUNT (₹)"\n`;
    const max = Math.max(tFormatData.debitItems.length, tFormatData.creditItems.length);

    for (let i = 0; i < max; i++) {
      const d = tFormatData.debitItems[i] || { name: "", amount: "" };
      const c = tFormatData.creditItems[i] || { name: "", amount: "" };
      csv += `"${d.name}","${d.amount !== "" ? Number(d.amount).toFixed(2) : ""}","${c.name}","${c.amount !== "" ? Number(c.amount).toFixed(2) : ""}"\n`;
    }

    csv += `"TOTAL DEBIT","${tFormatData.balancingTotal.toFixed(2)}","TOTAL CREDIT","${tFormatData.balancingTotal.toFixed(2)}"\n\n`;

    if (bookings && bookings.length > 0) {
      csv += `"TRIP BREAKDOWN"\n`;
      csv += `"Booking ID","Trip Date","Customer","Driver","Vehicle","Booking Freight (₹)","Taxi Freight (₹)","Discount (₹)","Net P&L (₹)","Status"\n`;
      bookings.forEach((b) => {
        csv += `"${b.bookingId}","${b.tripDate ? b.tripDate.split("T")[0] : ""}","${b.customerName}","${b.driverName}","${b.vehicleTypeName}","${Number(b.grossBookingFreight).toFixed(2)}","${Number(b.taxiFreight).toFixed(2)}","${Number(b.discountAmount).toFixed(2)}","${Number(b.bookingPNL).toFixed(2)}","${b.pnlStatus}"\n`;
      });
    }

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `Profit_Loss_Report_${toDate}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="page-content py-4 px-3 px-md-4">
      {/* ─── Header ────────────────────────────────────────────────────────── */}
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-4 gap-3">
        <div>
          <h4 className="mb-1 fw-bold text-dark d-flex align-items-center">
            <i className="bx bx-calculator me-2 text-primary"></i>
            Profit & Loss (P&L) Statement
          </h4>
          <p className="text-muted mb-0 small">
            Trip Freight Income vs Taxi Freight Driver Cost, Discounts & Net Financial Results
          </p>
        </div>

        <div className="d-flex gap-2 flex-wrap">
          <Button variant="outline-secondary" size="sm" onClick={handleExportCSV} disabled={loading}>
            <i className="bx bx-download me-1"></i> Export CSV
          </Button>
          <Button variant="outline-primary" size="sm" onClick={() => window.print()} disabled={loading}>
            <i className="bx bx-printer me-1"></i> Print
          </Button>
          <Button variant="primary" size="sm" onClick={fetchReport} disabled={loading}>
            {loading ? <Spinner animation="border" size="sm" /> : <><i className="bx bx-refresh me-1"></i> Refresh</>}
          </Button>
        </div>
      </div>

      {/* ─── Filter Bar ────────────────────────────────────────────────────── */}
      <Card className="shadow-sm border-0 mb-4 rounded-3">
        <Card.Body className="p-3">
          <Row className="g-3 align-items-end">
            <Col xs={12} sm={6} md={3}>
              <Form.Label className="small fw-semibold text-secondary mb-1">From Date</Form.Label>
              <Form.Control
                type="date"
                size="sm"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </Col>

            <Col xs={12} sm={6} md={3}>
              <Form.Label className="small fw-semibold text-secondary mb-1">To Date</Form.Label>
              <Form.Control
                type="date"
                size="sm"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            </Col>

            <Col xs={12} sm={6} md={2}>
              <Form.Label className="small fw-semibold text-secondary mb-1">Booking ID</Form.Label>
              <Form.Control
                type="number"
                size="sm"
                placeholder="Filter by Booking #"
                value={bookingFilter}
                onChange={(e) => setBookingFilter(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && fetchReport()}
              />
            </Col>

            <Col xs={12} sm={6} md={4}>
              <Form.Label className="small fw-semibold text-secondary mb-1">Quick Presets</Form.Label>
              <div className="d-flex gap-1 flex-wrap">
                <Button variant="outline-info" size="sm" className="py-1 px-2" onClick={() => handleQuickFilter("TODAY")}>Today</Button>
                <Button variant="outline-info" size="sm" className="py-1 px-2" onClick={() => handleQuickFilter("THIS_MONTH")}>This Month</Button>
                <Button variant="outline-info" size="sm" className="py-1 px-2" onClick={() => handleQuickFilter("LAST_30_DAYS")}>Last 30 Days</Button>
                <Button variant="outline-secondary" size="sm" className="py-1 px-2" onClick={() => handleQuickFilter("ALL")}>All Time</Button>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* ─── Bifurcation of Cash vs Online Freight ────────────────────────── */}
      <Row className="g-3 mb-3">
        <Col sm={6}>
          <div className="p-3 bg-light rounded-3 border-start border-4 border-warning d-flex justify-content-between align-items-center shadow-sm">
            <div>
              <span className="small text-muted text-uppercase fw-semibold d-block">💵 Cash Collected by Drivers (COD / Spot)</span>
              <span className="fs-5 fw-bold text-dark">
                ₹{cashOnlineCollections.cash.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </div>
            <Badge bg="warning" text="dark" className="px-2 py-1 fw-bold">Physical Cash</Badge>
          </div>
        </Col>
        <Col sm={6}>
          <div className="p-3 bg-light rounded-3 border-start border-4 border-info d-flex justify-content-between align-items-center shadow-sm">
            <div>
              <span className="small text-muted text-uppercase fw-semibold d-block">💳 Online Payment Gateways (UPI / Bank / PG)</span>
              <span className="fs-5 fw-bold text-primary">
                ₹{cashOnlineCollections.online.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </div>
            <Badge bg="info" className="px-2 py-1 fw-bold">Online Digital</Badge>
          </div>
        </Col>
      </Row>

      {/* ─── KPI Metrics Cards ────────────────────────────────────────────── */}
      <Row className="g-3 mb-4">
        <Col xs={12} sm={6} lg={3}>
          <Card className="border-0 shadow-sm rounded-3 h-100 bg-white">
            <Card.Body className="p-3">
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <span className="text-muted small fw-medium text-uppercase">Gross Booking Freight</span>
                  <h4 className="fw-bold mb-0 text-success mt-1">
                    ₹{Number(summary.totalBookingFreight || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </h4>
                  <span className="small text-muted">{summary.totalTrips || 0} Total Trips</span>
                </div>
                <div className="rounded-circle bg-success bg-opacity-10 p-3 text-success">
                  <i className="bx bx-trending-up fs-3"></i>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} sm={6} lg={3}>
          <Card className="border-0 shadow-sm rounded-3 h-100 bg-white">
            <Card.Body className="p-3">
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <span className="text-muted small fw-medium text-uppercase">Taxi Freight (Driver)</span>
                  <h4 className="fw-bold mb-0 text-primary mt-1">
                    ₹{Number(summary.totalTaxiFreight || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </h4>
                  <span className="small text-muted">Driver Hire Share</span>
                </div>
                <div className="rounded-circle bg-primary bg-opacity-10 p-3 text-primary">
                  <i className="bx bx-car fs-3"></i>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} sm={6} lg={3}>
          <Card className="border-0 shadow-sm rounded-3 h-100 bg-white">
            <Card.Body className="p-3">
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <span className="text-muted small fw-medium text-uppercase">Discounts & Offers</span>
                  <h4 className="fw-bold mb-0 text-warning mt-1">
                    ₹{Number(summary.totalDiscounts || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </h4>
                  <span className="small text-muted">Promotional Subsidies</span>
                </div>
                <div className="rounded-circle bg-warning bg-opacity-10 p-3 text-warning">
                  <i className="bx bx-purchase-tag fs-3"></i>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} sm={6} lg={3}>
          <Card className={`border-0 shadow-sm rounded-3 h-100 text-white ${tFormatData.isProfit ? "bg-gradient-success" : "bg-gradient-danger"}`}
                style={{ backgroundColor: tFormatData.isProfit ? "#198754" : "#dc3545" }}>
            <Card.Body className="p-3">
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <span className="small fw-medium text-uppercase opacity-75">
                    {tFormatData.isProfit ? "Net Profit" : "Net Loss"}
                  </span>
                  <h4 className="fw-bold mb-0 mt-1 text-white">
                    ₹{Math.abs(tFormatData.netProfitLoss).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </h4>
                  <span className="small opacity-75">
                    {tFormatData.isProfit ? "Profitable Period" : "Loss Incurred"}
                  </span>
                </div>
                <div className="rounded-circle bg-white bg-opacity-25 p-3 text-white">
                  <i className={`bx ${tFormatData.isProfit ? "bx-badge-check" : "bx-error-circle"} fs-3`}></i>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* ─── View Selector Tabs ───────────────────────────────────────────── */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="btn-group">
          <Button
            variant={viewMode === "T-Shape" ? "primary" : "outline-secondary"}
            size="sm"
            onClick={() => setViewMode("T-Shape")}
          >
            <i className="bx bx-columns me-1"></i> T-Format Statement
          </Button>
          <Button
            variant={viewMode === "Trips" ? "primary" : "outline-secondary"}
            size="sm"
            onClick={() => setViewMode("Trips")}
          >
            <i className="bx bx-list-ul me-1"></i> Trip-Wise Breakdown ({bookings.length})
          </Button>
        </div>

        {viewMode === "Trips" && (
          <div style={{ maxWidth: 280 }}>
            <Form.Control
              type="text"
              size="sm"
              placeholder="Search driver, customer, vehicle..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        )}
      </div>

      {/* ─── Main Content Views ───────────────────────────────────────────── */}
      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="text-muted mt-2 small">Calculating Profit & Loss Statement...</p>
        </div>
      ) : error ? (
        <Alert variant="info" className="text-center py-4">{error}</Alert>
      ) : viewMode === "T-Shape" ? (
        /* ─── T-Shape Format View ────────────────────────────────────────── */
        <Card className="shadow-sm border-0 rounded-3 overflow-hidden">
          <Card.Header className="bg-light py-3 border-bottom d-flex justify-content-between align-items-center">
            <h6 className="mb-0 fw-bold text-dark">
              <i className="bx bx-book-open me-2 text-primary"></i>
              Profit & Loss Statement (T-Account Format)
            </h6>
            <Badge bg={tFormatData.isProfit ? "success" : "danger"} className="px-2 py-1">
              {tFormatData.isProfit ? "NET PROFIT" : "NET LOSS"}
            </Badge>
          </Card.Header>

          <Card.Body className="p-0">
            <Row className="g-0">
              {/* Left Column: DEBIT (Expenses & Net Profit) */}
              <Col md={6} className="border-end">
                <div className="bg-light p-2 text-center fw-bold text-danger border-bottom">
                  <i className="bx bx-minus-circle me-1"></i> DEBIT (Expenses & Direct Costs)
                </div>
                <Table hover responsive className="mb-0 align-middle">
                  <thead className="table-light small">
                    <tr>
                      <th style={{ width: "65%" }}>Particulars</th>
                      <th className="text-end" style={{ width: "35%" }}>Amount (₹)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tFormatData.debitItems.map((item, idx) => (
                      <tr key={idx} className={item.isHighlight ? "table-success fw-bold" : ""}>
                        <td>
                          <div>{item.name}</div>
                          <span className="badge bg-light text-secondary border small">{item.category}</span>
                        </td>
                        <td className="text-end fw-semibold">
                          ₹{Number(item.amount).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Col>

              {/* Right Column: CREDIT (Income & Net Loss) */}
              <Col md={6}>
                <div className="bg-light p-2 text-center fw-bold text-success border-bottom">
                  <i className="bx bx-plus-circle me-1"></i> CREDIT (Incomes & Revenues)
                </div>
                <Table hover responsive className="mb-0 align-middle">
                  <thead className="table-light small">
                    <tr>
                      <th style={{ width: "65%" }}>Particulars</th>
                      <th className="text-end" style={{ width: "35%" }}>Amount (₹)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tFormatData.creditItems.map((item, idx) => (
                      <tr key={idx} className={item.isHighlight ? "table-danger fw-bold" : ""}>
                        <td>
                          <div>{item.name}</div>
                          <span className="badge bg-light text-secondary border small">{item.category}</span>
                        </td>
                        <td className="text-end fw-semibold">
                          ₹{Number(item.amount).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Col>
            </Row>

            {/* Balancing Footer */}
            <div className="bg-dark text-white p-3 d-flex flex-wrap justify-content-between align-items-center">
              <div className="d-flex align-items-center gap-4">
                <div>
                  <span className="small text-white-50 text-uppercase d-block">Total Debit Side</span>
                  <span className="fw-bold fs-5">₹{tFormatData.balancingTotal.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </div>
                <div className="text-white-50">|</div>
                <div>
                  <span className="small text-white-50 text-uppercase d-block">Total Credit Side</span>
                  <span className="fw-bold fs-5">₹{tFormatData.balancingTotal.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </div>
              </div>

              <div className="text-end">
                <span className="small text-white-50 text-uppercase d-block">Net Result</span>
                <span className={`fw-bold fs-5 ${tFormatData.isProfit ? "text-success" : "text-danger"}`}>
                  {tFormatData.isProfit ? "+ ₹" : "- ₹"}
                  {Math.abs(tFormatData.netProfitLoss).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  {" "}({tFormatData.isProfit ? "Net Profit" : "Net Loss"})
                </span>
              </div>
            </div>
          </Card.Body>
        </Card>
      ) : (
        /* ─── Trip-Wise Detailed Breakdown ────────────────────────────────── */
        <Card className="shadow-sm border-0 rounded-3 overflow-hidden">
          <Card.Header className="bg-light py-3 border-bottom">
            <h6 className="mb-0 fw-bold text-dark">
              <i className="bx bx-list-check me-2 text-primary"></i>
              Trip-Wise P&L Calculation Breakdown
            </h6>
          </Card.Header>
          <Card.Body className="p-0">
            <div className="table-responsive">
              <Table hover className="mb-0 align-middle">
                <thead className="table-light small text-uppercase">
                  <tr>
                    <th># ID</th>
                    <th>Date</th>
                    <th>Customer</th>
                    <th>Driver</th>
                    <th>Vehicle</th>
                    <th className="text-end text-success">Booking Freight</th>
                    <th className="text-end text-primary">Taxi Freight</th>
                    <th className="text-end text-warning">Discount</th>
                    <th className="text-end fw-bold">Net P&L</th>
                    <th className="text-center">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredBookings.length === 0 ? (
                    <tr>
                      <td colSpan={10} className="text-center py-4 text-muted">No trip records found.</td>
                    </tr>
                  ) : (
                    filteredBookings.map((b) => (
                      <tr key={b.bookingId}>
                        <td className="fw-bold">#{b.bookingId}</td>
                        <td className="small text-muted">{b.tripDate ? b.tripDate.split("T")[0] : "-"}</td>
                        <td>{b.customerName || "-"}</td>
                        <td>{b.driverName || "-"}</td>
                        <td><Badge bg="light" className="text-dark border">{b.vehicleTypeName || "Taxi"}</Badge></td>
                        <td className="text-end fw-semibold text-success">₹{Number(b.grossBookingFreight || 0).toFixed(2)}</td>
                        <td className="text-end fw-semibold text-primary">₹{Number(b.taxiFreight || 0).toFixed(2)}</td>
                        <td className="text-end text-warning">{Number(b.discountAmount || 0) > 0 ? `₹${Number(b.discountAmount).toFixed(2)}` : "-"}</td>
                        <td className={`text-end fw-bold ${b.pnlStatus === "Profit" ? "text-success" : "text-danger"}`}>
                          {b.pnlStatus === "Profit" ? "+" : "-"}₹{Math.abs(Number(b.bookingPNL || 0)).toFixed(2)}
                        </td>
                        <td className="text-center">
                          <Badge bg={b.pnlStatus === "Profit" ? "success" : "danger"}>
                            {b.pnlStatus}
                          </Badge>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>
          </Card.Body>
        </Card>
      )}
    </div>
  );
};

export default ProfitLossReport;
